import { formatDateTimeAt } from '@/shared/formatters/date';
import { rotuloMotivoEncerramentoFisico } from '@/shared/utils/rotulosCitacaoRequerido';
import {
  atoNoEventoDaCitacao,
  linhaDoAtoDaCitacao,
  seloDaCitacao,
} from '@/shared/utils/situacaoDaCitacao';
import { capitalizarPrimeiraLetra, rotularEnum } from '@/shared/utils/text';
import type { ResumoDaCitacaoDto } from '@/types/arbitral/citacaoRequerido';

import type {
  CitacaoDaComprovacaoDto,
  ComprovacaoParteDto,
  EventoDaCitacaoDto,
} from '../types/certificadoAcessosDto';
import type { VarianteStatusCertificado } from '../types/certificadoAcessosTypes';

/*
 * Seção 3 do Certificado de Acessos, na parte que diz da CITAÇÃO (D-04, Jonathan, 29/10): o selo da
 * parte É a situação da citação — a mesma da Secretaria e do árbitro —, com a data e a linha do ato
 * no "Citado" e o motivo no "Aguardando AR". A linha "Citação no processo" saiu: o selo já a diz.
 *
 * O core porta este arquivo para o PDF (`templates/documento/visao/citacaoNaComprovacao.ts`) e a
 * paridade é conferida por fixture: mudou aqui, regere o `esperado.json` de lá.
 */

type CitacaoComResumo = CitacaoDaComprovacaoDto & ResumoDaCitacaoDto;

/** O resumo da citação vem do core da D-04 em diante; o anterior manda só o status. */
function temResumo(citacao: CitacaoDaComprovacaoDto): citacao is CitacaoComResumo {
  return typeof citacao.situacao === 'string';
}

function resumoDa(citacao: CitacaoDaComprovacaoDto | null | undefined): CitacaoComResumo | null {
  return citacao && temResumo(citacao) ? citacao : null;
}

/**
 * O selo da parte na Seção 3 da citação. `null` quando o core não manda o resumo (Certificado da
 * Sentença, ou core anterior à D-04): quem chama fica com o selo da comprovação.
 */
export function seloDaCitacaoNaComprovacao(
  parte: ComprovacaoParteDto,
): { label: string; variante: VarianteStatusCertificado } | null {
  const resumo = resumoDa(parte.citacao);
  return resumo ? seloDaCitacao(resumo) : null;
}

function descricaoDoEvento(evento: EventoDaCitacaoDto): string {
  if (evento.tipo === 'CITACAO_CONFIRMADA' || evento.tipo === 'CITACAO_REGISTRADA') {
    const ato = atoNoEventoDaCitacao({
      ato: evento.ato ?? null,
      meio: evento.meio,
      dataAto: evento.dataAto ?? null,
    });
    return ato ? `Citado — ${ato}` : 'Citado';
  }
  if (evento.tipo === 'CICLO_DIGITAL_ENCERRADO') {
    return evento.motivo
      ? `Ciclo digital encerrado — ${rotuloMotivoEncerramentoFisico(evento.motivo)}`
      : 'Ciclo digital encerrado';
  }
  // Ato que esta tela ainda não conhece sai pelo nome do enum, nunca some em silêncio.
  return capitalizarPrimeiraLetra(rotularEnum(evento.tipo));
}

/**
 * O motivo só fala enquanto a física dura: citado depois, ele é histórico de por que o ciclo foi ao
 * AR, e não descreve a citação de hoje.
 */
function linhaDoMotivo(resumo: CitacaoComResumo): string[] {
  const motivo = resumo.motivoEncerramentoFisico;
  if (resumo.situacao !== 'AGUARDANDO_AR' || !motivo) return [];
  return [`Motivo: ${rotuloMotivoEncerramentoFisico(motivo)}`];
}

function linhasDoResumo(resumo: CitacaoComResumo | null): string[] {
  if (!resumo) return [];
  const ato = linhaDoAtoDaCitacao(resumo);
  return [...(ato ? [ato] : []), ...linhaDoMotivo(resumo)];
}

/**
 * Linhas abaixo do selo: a linha do ato (no "Citado") ou o motivo (no "Aguardando AR"), e os atos
 * da citação na trilha, com data e hora de Brasília. Sem a citação no DTO (Certificado da
 * Sentença), nada.
 *
 * @example linhasDaCitacao({ situacao: 'AGUARDANDO_AR', motivoEncerramentoFisico: 'INCONCLUSIVO', eventos: [], ... })
 * // ['Motivo: Sem retorno de entrega']
 */
export function linhasDaCitacao(citacao: CitacaoDaComprovacaoDto | null | undefined): string[] {
  if (!citacao) return [];
  return [
    ...linhasDoResumo(resumoDa(citacao)),
    ...citacao.eventos.map((e) => `${descricaoDoEvento(e)}, em ${formatDateTimeAt(e.em)}`),
  ];
}
